import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { type ReactNode, useState } from "react";
import { useTranslation } from "@i18n/hooks";

type DialogSettings = {
	title: string;
	content: string;
	dialogCallback: DialogCallback;
	affirmativeText?: string;
	negativeText?: string;
};

type DialogHookProps = {
	dialogComponent: ReactNode;
	showDialog: (settings: DialogSettings) => void;
};

type DialogResult = "Accept" | "Deny";

type DialogCallback = (result: DialogResult) => void;

type DialogState = {
	dialogCallback: DialogCallback;
	dialogVisible: boolean;
} & DialogSettings;

const INITIAL_STATE = {
	dialogCallback: () => {},
	title: "Untitled",
	content: "No content",
	affirmativeText: undefined,
	negativeText: undefined,
	dialogVisible: false,
};

export const useDialogLogic = (): DialogHookProps => {
	const { t } = useTranslation("common");
	const [dialogState, setDialogState] = useState<DialogState>(INITIAL_STATE);

	return {
		dialogComponent: (
			<Dialog
				header={dialogState.title}
				visible={dialogState.dialogVisible}
				footer={
					<div>
						<Button
							label={dialogState.negativeText ?? t("actions.cancel")}
							icon="pi pi-times"
							onClick={() => {
								dialogState.dialogCallback("Deny");
								setDialogState(INITIAL_STATE);
							}}
							className="p-button-text"
						/>
						<Button
							label={dialogState.affirmativeText ?? t("actions.accept")}
							icon="pi pi-check"
							onClick={() => {
								dialogState.dialogCallback("Accept");
								setDialogState(INITIAL_STATE);
							}}
							autoFocus
						/>
					</div>
				}
				onHide={() => {
					dialogState.dialogCallback("Deny");
					setDialogState(INITIAL_STATE);
				}}
			>
				<div dangerouslySetInnerHTML={{ __html: dialogState.content }} />
			</Dialog>
		),

		showDialog: (settings: DialogSettings) => {
			setDialogState({
				...settings,
				dialogVisible: true,
			});
		},
	};
};
